import * as auth from "./authService";
import jwtDecode from 'jwt-decode';
import { encrypt, decrypt } from '../utilities/cipher';


export const LOGOUT_EVENT_KEY = 'centric-logout';
const TOKEN_STORAGE_KEY = 'centric-token';
const REFRESH_STORAGE_KEY = 'centric-refresh';
const REFRESH_MUTEX = 'refreshing-token';

const validateToken = (token) => {
    const { exp } = jwtDecode(token);
    if (!exp) return false;
    const delay = (exp * 1000) - Date.now();

    return (delay > 0) && delay;
}

const calculateTokenExpiry = (token) => {
    const { exp } = jwtDecode(token);

    return exp && new Date(exp * 1000);
}

const tokenStorage = () => {
    let authToken = null;
    let refreshToken = null;
    let refreshTimeoutId;
    let ssoTokens = {};

    const destroyAuthTokens = () => {
        authToken = null;
        refreshToken = null;
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.localStorage.removeItem(REFRESH_STORAGE_KEY);
    };

    const storeAuthTokens = (token, refresh) => {
        authToken = token;
        refreshToken = refresh;
        window.localStorage.setItem(TOKEN_STORAGE_KEY, encrypt(token));
        window.localStorage.setItem(REFRESH_STORAGE_KEY, encrypt(refresh));
    };

    const loadAuthTokens = () => {
        let token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
        if (token) {
            token = decrypt(token);
            if (validateToken(token)) {
                authToken = token;
            }
        }
        let refresh = window.localStorage.getItem(REFRESH_STORAGE_KEY);
        if (refresh) {
            refresh = decrypt(refresh);
            const delay = validateToken(refresh);
            if (delay) {
                refreshToken = refresh;
                refreshAuthToken(delay);
            }
        }
    };

    const getAuthToken = () => {
        if (!authToken) loadAuthTokens();
        return authToken;
    };

    const getRefreshToken = () => {
        if (!refreshToken) loadAuthTokens();
        return refreshToken;
    };

    const setAuthToken = ({access_token, refresh_token = null, expires_in = null}) => {
        clearRefreshAuthToken();
        storeAuthTokens(access_token, refresh_token);
        if (!!expires_in) {
            refreshAuthToken(expires_in * 1000);
        }
        return true;
    };

    const eraseAuthToken = () => {
        clearRefreshAuthToken();
        destroyAuthTokens();
        window.localStorage.setItem(LOGOUT_EVENT_KEY, Date.now());
        return true;
    };

    const refreshAuthToken = (delay) => {
        // let's refresh at 80% of validity
        refreshTimeoutId = window.setTimeout(() => {
            window.localStorage.setItem(REFRESH_MUTEX, refreshToken);
            return auth.refreshAuthToken();
        }, delay * 0.8);
    };

    const clearRefreshAuthToken = () => {
        if (refreshTimeoutId) {
            window.clearTimeout(refreshTimeoutId);
            refreshTimeoutId = null;
        }
    };

    const getSSOToken = async (system) => {
        if (!ssoTokens[system]) {
            const data = await auth.getSSOToken(system);
            setSSOToken(system, data);
        }
        return ssoTokens[system].accessToken;
    };

    const setSSOToken = (system, {access_token, refresh_token}) => {
        const delay = validateToken(access_token);
        ssoTokens[system] = {
            accessToken: access_token,
            accessTokenExpires: calculateTokenExpiry(access_token),
            refreshToken: refresh_token,
            refreshTokenExpires: calculateTokenExpiry(refresh_token),
            refreshId: refreshSSOToken(system, delay)
        };
        return true;
    };

    const eraseSSOToken = (system) => {
        if (!ssoTokens[system]) return true;
        if (!!ssoTokens[system].refreshId) {
            clearTimeout(ssoTokens[system].refreshId);
        }

        delete ssoTokens[system];
        return true;
    };

    const getSSORefreshToken = (system) => (ssoTokens[system].refreshToken || null);

    const refreshSSOToken = (system, delay) => {
        return window.setTimeout(() => {
            return auth.refreshSSOToken(system);
        }, delay * 0.8)
    };

    const eraseTokens = () => {
        eraseAuthToken();
        Object.keys(ssoTokens).forEach(eraseSSOToken);
    };

    window.addEventListener('storage', ({key}) => {
        switch (key) {
            case LOGOUT_EVENT_KEY:
                eraseTokens();
                break;
            case REFRESH_MUTEX:
                clearRefreshAuthToken();
                break;

            default: break;
        }
    });

    return {
        getAuthToken,
        setAuthToken,
        eraseAuthToken,
        getRefreshToken,

        getSSOToken,
        setSSOToken,
        eraseSSOToken,
        getSSORefreshToken,

        eraseTokens,
    };
}

export default tokenStorage();
